import Link from 'next/link';
import { CityMetadata, RegionSettings, Settings } from '../../data/api';
import Loading from '../Loading';
import { MetricsData } from '../metrics/use-metrics';
import styles from '../../styles/MetricTable.module.scss';
import { useMemo, useState } from 'react';
import { orderBy } from 'lodash-es';
import ColumnHeader from '../ColumnHeader';
import NUMBER_FORMATTERS from '../../utils/number-format';
import { useLinearUnitsInfo } from '../../utils/use-linear-units';
import { convertLinearUnitsForDisplay } from '../../utils/units-conversion';

export interface MetricTableProps {
    region: RegionSettings;
    citySettings: { [slug: string]: Settings };
    metricsMap: Record<string, MetricsData | null> | null;
}

export const unitsShorthand = {
    kilometers: 'km',
    minutes: 'min',
    miles: 'mi',
    trips: 'trips',
};

type Column =
    | 'name'
    | 'population'
    | 'totalTripCount'
    | 'averageTripsPerDay'
    | 'medianTripDuration'
    | 'medianTripDistance'
    | 'tripDistance'
    | 'tripsPerVehiclePerDay'
    | 'meanActiveVehicles';
type Sorting = [column: Column, order: 'asc' | 'desc'];

function sorting(column: Column) {
    return ({
        city,
        metrics,
    }: {
        city: CityMetadata;
        metrics: MetricsData | null;
    }): string | number => {
        switch (column) {
            case 'name':
            case 'population':
                return city[column];
            case 'totalTripCount':
                return metrics?.totalTripCount ?? 0;
            case 'averageTripsPerDay':
                return metrics?.averageTripsPerDay ?? 0;
            case 'medianTripDuration':
                return metrics?.tripDuration.median ?? 0;
            case 'medianTripDistance':
                return metrics?.tripDistance.median ?? 0;
            case 'tripDistance':
                return metrics?.tripDistance.sumTotal ?? 0;
            case 'tripsPerVehiclePerDay':
                return metrics?.tripsPerVehiclePerDay.average ?? 0;
            case 'meanActiveVehicles':
                return metrics?.meanActiveVehiclesPerDay.average ?? 0;
        }
    };
}

function MetricTable({ citySettings, metricsMap, region }: MetricTableProps) {
    const [[sortColumn, sortOrder], setSorting] = useState<Sorting>([
        'name',
        'asc',
    ]);
    const linearUnits = useLinearUnitsInfo();

    const sortedMetrics = useMemo(
        () =>
            metricsMap &&
            (orderBy(
                region.city_metadata.map(city => ({
                    city,
                    metrics: metricsMap[city.slug],
                })),
                sorting(sortColumn),
                sortOrder
            ) as { city: CityMetadata; metrics: MetricsData | null }[]),
        [region.city_metadata, metricsMap, sortColumn, sortOrder]
    );
    const metrics = metricsMap
        ? (Object.values(metricsMap).filter(m => m) as MetricsData[])
        : [];

    const regionMeanActiveVehiclesPerDay = metrics
        .map(metrics => metrics.meanActiveVehiclesPerDay.average)
        .reduce((partialSum, a) => partialSum + a, 0);

    const regionWeightedTVD =
        metrics
            .map(
                metrics =>
                    metrics.tripsPerVehiclePerDay.average *
                    metrics.meanActiveVehiclesPerDay.average
            )
            .reduce((partialSum, a) => partialSum + a, 0) /
        regionMeanActiveVehiclesPerDay;

    const regionTripCount = metrics
        .map(metrics => metrics.totalTripCount)
        .reduce((partialSum, a) => partialSum + a, 0);

    const firstMetric: MetricsData | null = metrics[0] ?? null;
    const metricLinearUnits =
        firstMetric?.tripDistance?.xUnit ?? linearUnits.units;
    const regionTotalTripDistance = convertLinearUnitsForDisplay(
        Object.values(metrics ?? {})
            .map(m => m?.tripDistance.sumTotal ?? 0)
            .reduce((partialSum, a) => partialSum + a, 0),
        metricLinearUnits,
        linearUnits
    );
    const regionTripsPerDay = metrics
        .map(metrics => metrics.tripsPerDay.average)
        .reduce((partialSum, a) => partialSum + a, 0);

    const headerProps = {
        setSorting,
        sorting: [sortColumn, sortOrder] as const,
    };

    return (
        <div className={styles.scrollableTable}>
            <Loading kind="overTable" loading={!metricsMap} />
            <table className={styles.table}>
                <thead>
                    <tr>
                        <ColumnHeader<Column>
                            column="name"
                            {...headerProps}
                            text="City"
                            secondaryText="Population"
                            tooltipText="Population data is from Census Reporter, Australian Bureau of Statistics and NZ Stats."
                        />
                        <ColumnHeader<Column>
                            column="meanActiveVehicles"
                            {...headerProps}
                            text="Active Vehicles"
                            secondaryText="avg"
                            tooltipText="Average number of active shared vehicles. Active vehicles refer to vehicles that are available for rent or rented."
                        />
                        <ColumnHeader<Column>
                            column="tripsPerVehiclePerDay"
                            {...headerProps}
                            text="TVD"
                            secondaryText="avg"
                            tooltipText="Trips Per Vehicle Per Day. Total shared vehicle trips divided by the average active vehicles divided by the number of days. This metric is most commonly used as an indicator of how often vehicles are being used."
                        />
                        <ColumnHeader<Column>
                            column="averageTripsPerDay"
                            {...headerProps}
                            text="Trips Per Day"
                            secondaryText="avg"
                            tooltipText="Average daily shared vehicle trips for the time period selected."
                        />
                        <ColumnHeader<Column>
                            column="totalTripCount"
                            {...headerProps}
                            text="Trips"
                            secondaryText="total"
                            tooltipText="Total shared vehicle trips for the time period selected."
                        />
                        <ColumnHeader<Column>
                            column="tripDistance"
                            {...headerProps}
                            text="Total Distance"
                            secondaryText={unitsShorthand[linearUnits.units]}
                            tooltipText="Total distance ridden by shared vehicles for the time period selected."
                        />
                    </tr>
                </thead>
                <tbody>
                    <tr className={styles.combinedRegionRow}>
                        <td>
                            <div className={styles.namePopCellText}>
                                <span className={styles.regionName}>
                                    {region.name}
                                </span>
                                <span className={styles.population}>
                                    {region.population.toLocaleString()}
                                </span>
                            </div>
                        </td>
                        <td>
                            {NUMBER_FORMATTERS.integer.format(
                                regionMeanActiveVehiclesPerDay
                            )}
                        </td>
                        <td>
                            {NUMBER_FORMATTERS.integer.format(
                                regionWeightedTVD
                            )}
                        </td>
                        <td>
                            {NUMBER_FORMATTERS.integer.format(
                                regionTripsPerDay
                            )}
                        </td>
                        <td>
                            {NUMBER_FORMATTERS.integer.format(regionTripCount)}
                        </td>
                        <td>
                            {NUMBER_FORMATTERS.integer.format(
                                regionTotalTripDistance
                            )}
                        </td>
                    </tr>

                    {sortedMetrics?.map(({ city, metrics }, index) => (
                        <MetricRow
                            position={index + 1}
                            key={city.slug}
                            cityMetadata={city}
                            settings={citySettings[city.slug]}
                            metrics={metrics}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default MetricTable;

export interface MetricRowProps {
    cityMetadata: CityMetadata;
    settings: Settings | null;
    metrics: MetricsData | null;
    position: number;
}

function MetricRow({
    cityMetadata,
    settings,
    metrics,
    position,
}: MetricRowProps) {
    const cityDashboardEnabled = settings != null;

    const namePopCell = (
        <td>
            <div className={styles.namePopCellContainer}>
                <span>{position}</span>
                <div className={styles.namePopCellText}>
                    <span className={styles.cityName}>
                        {cityDashboardEnabled ? (
                            <Link
                                href={`/${cityMetadata.slug}`}
                                key={cityMetadata.slug}
                            >
                                <a>{cityMetadata.name}</a>
                            </Link>
                        ) : (
                            <> {cityMetadata.name} </>
                        )}
                    </span>
                    <span className={styles.population}>
                        {cityMetadata.population.toLocaleString()}
                    </span>
                </div>
            </div>
        </td>
    );

    if (!metrics) {
        return (
            <tr>
                {namePopCell}
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
            </tr>
        );
    }

    return (
        <tr>
            {namePopCell}
            <td>
                {NUMBER_FORMATTERS.integer.format(
                    metrics.meanActiveVehiclesPerDay.average
                )}
            </td>
            <td>
                {NUMBER_FORMATTERS.float.format(
                    metrics.tripsPerVehiclePerDay.average
                )}
            </td>
            <td>
                {metrics.tripsPerDay.average === 0
                    ? '<50'
                    : NUMBER_FORMATTERS.integer.format(
                          metrics.tripsPerDay.average
                      )}
            </td>
            <td>{NUMBER_FORMATTERS.integer.format(metrics.totalTripCount)}</td>
            <td>
                {NUMBER_FORMATTERS.integer.format(
                    metrics.tripDistance.sumTotal
                )}
            </td>
        </tr>
    );
}
